import React, { type JSX } from 'react'
import classNames from 'classnames'
import useUniqueId from '../hooks/use-unique-id'
import { useTranslations } from '../hooks'
import { Input } from './../atoms'

export enum InputType {
  EMAIL = 'email',
  NUMBER = 'number',
  PASSWORD = 'password',
  SEARCH = 'search',
  TEL = 'tel',
  TEXT = 'text',
}

export enum AutoCompleteType {
  NONE = '',
  OFF = 'autocomplete-off',
  GIVEN_NAME = 'given-name',
  FAMILY_NAME = 'family-name',
  STREET_ADDRESS = 'street-address',
  POSTAL_CODE = 'postal-code',
  ADDRESS_LEVEL2 = 'address-level2',
  EMAIL = 'email',
  TEL = 'tel',
}

type Props = {
  id?: string
  type: InputType
  name?: string
  label: string | JSX.Element
  value: string
  onChange?: (arg0: React.ChangeEvent<HTMLInputElement>) => void
  onKeyDown?: (arg0: React.KeyboardEvent<HTMLInputElement>) => void
  onFocus?: (arg0: React.FocusEvent<HTMLInputElement>) => void
  onBlur?: (arg0: React.FocusEvent<HTMLInputElement>) => void
  error?: boolean
  customErrorText?: string
  onValidation?: (arg0: boolean) => void
  placeHolder?: string
  mandatory?: boolean
  autoComplete?: AutoCompleteType
  readOnly?: boolean
  hiddenLabel?: boolean
  className?: string
  inputClassName?: string
  autoFocus?: boolean
  maxLength?: number
  validate?: (type: InputType, value: string) => boolean
}

const defaultValidation = (type: InputType, value: string) => {
  if (!value) {
    return true
  }

  if (type === InputType.EMAIL) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
  } else {
    return true
  }
}

const getAutoCompleteFromType = (type: InputType) => {
  switch (type) {
    case InputType.EMAIL:
      return AutoCompleteType.EMAIL
    case InputType.NUMBER:
      return AutoCompleteType.TEL
    default:
      return AutoCompleteType.NONE
  }
}

export const TextInput = ({
  id,
  type,
  name,
  label,
  value,
  onChange,
  onKeyDown,
  onFocus,
  onBlur,
  error = false,
  customErrorText = '',
  onValidation,
  placeHolder = '',
  mandatory = false,
  autoComplete = getAutoCompleteFromType(type),
  readOnly = false,
  hiddenLabel = false,
  className = '',
  inputClassName = '',
  autoFocus = false,
  validate = defaultValidation,
  maxLength,
}: Props) => {
  const { translate } = useTranslations()
  const uniqueId = useUniqueId(type)
  const inputId = id ?? uniqueId
  const hasDefaultErrorText = type === InputType.EMAIL

  const getErrorText = () => {
    switch (type) {
      case InputType.EMAIL:
        return translate('Error_Email')
      default:
        return ''
    }
  }

  const onFocusHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    onFocus?.(e)
  }

  const onBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    onValidation?.(validate(type, value))

    onBlur?.(e)
  }

  return (
    <div
      className={classNames(`${className} c-form__item`, {
        'c-form__item--mandatory': mandatory,
        'c-form__error': error,
      })}>
      <label
        htmlFor={inputId}
        className={classNames({
          'u-sr-only': hiddenLabel,
        })}>
        {label}
      </label>
      <Input
        type={type}
        id={inputId}
        value={value}
        onChange={e => (onChange ? onChange(e) : null)}
        onKeyDown={e => (onKeyDown ? onKeyDown(e) : null)}
        onFocus={onFocusHandler}
        onBlur={onBlurHandler}
        className={inputClassName}
        required={mandatory}
        name={name}
        placeHolder={placeHolder}
        autoComplete={autoComplete}
        readOnly={readOnly}
        autoFocus={autoFocus}
        maxLength={maxLength}
      />
      {error && (hasDefaultErrorText || customErrorText.length > 0) && (
        <p className="c-form__error-text">
          {customErrorText.length > 0 ? customErrorText : getErrorText()}
        </p>
      )}
    </div>
  )
}

export default TextInput
